// Pure reducer driving the download queue. No React imports: this module is
// framework-agnostic so it can be unit-tested in isolation and wired into a
// useReducer-based provider by the caller.
//
// item: { id, episode, status: 'queued'|'downloading'|'done'|'error', progress: number, error: string|null }

export const createInitialState = () => ({ items: [] });

// Appends `episode` if its id isn't already present (in any status);
// otherwise returns `items` unchanged (same reference) so callers can detect
// a no-op via identity.
function enqueueOne(items, episode) {
  if (items.some((it) => it.id === episode.id)) return items;
  return [...items, { id: episode.id, episode, status: 'queued', progress: 0, error: null }];
}

export function downloadQueueReducer(state, action) {
  switch (action.type) {
    case 'ENQUEUE': {
      const items = enqueueOne(state.items, action.episode);
      return items === state.items ? state : { ...state, items };
    }

    case 'START': {
      // Invariant: at most one item is ever 'downloading' at a time.
      if (state.items.some((it) => it.status === 'downloading')) return state;
      const idx = state.items.findIndex((it) => it.id === action.id && it.status === 'queued');
      if (idx === -1) return state;
      return {
        ...state,
        items: state.items.map((it, i) => (i === idx ? { ...it, status: 'downloading' } : it)),
      };
    }

    case 'PROGRESS': {
      const idx = state.items.findIndex((it) => it.id === action.id);
      if (idx === -1) return state;
      return {
        ...state,
        items: state.items.map((it, i) => (i === idx ? { ...it, progress: action.pct } : it)),
      };
    }

    case 'COMPLETE': {
      const idx = state.items.findIndex((it) => it.id === action.id);
      if (idx === -1) return state;
      return {
        ...state,
        items: state.items.map((it, i) => (i === idx ? { ...it, status: 'done', progress: 100 } : it)),
      };
    }

    case 'FAIL': {
      const idx = state.items.findIndex((it) => it.id === action.id);
      if (idx === -1) return state;
      return {
        ...state,
        items: state.items.map((it, i) => (i === idx ? { ...it, status: 'error', error: action.message } : it)),
      };
    }

    case 'CANCEL': {
      const items = state.items.filter((it) => it.id !== action.id);
      return items.length === state.items.length ? state : { ...state, items };
    }

    case 'RETRY': {
      const idx = state.items.findIndex((it) => it.id === action.id && it.status === 'error');
      if (idx === -1) return state;
      return {
        ...state,
        items: state.items.map((it, i) => (i === idx ? { ...it, status: 'queued', progress: 0, error: null } : it)),
      };
    }

    case 'RESTORE': {
      let items = state.items;
      for (const episode of action.episodes) {
        items = enqueueOne(items, episode);
      }
      return items === state.items ? state : { ...state, items };
    }

    default:
      return state;
  }
}

export const selectActive = (s) => s.items.find((it) => it.status === 'downloading') || null;

export const selectNextQueued = (s) => s.items.find((it) => it.status === 'queued') || null;

export const selectPendingCount = (s) =>
  s.items.filter((it) => it.status === 'queued' || it.status === 'downloading').length;

export const selectErrorCount = (s) => s.items.filter((it) => it.status === 'error').length;

export const selectPersistedIds = (s) =>
  s.items.filter((it) => it.status === 'queued' || it.status === 'downloading').map((it) => it.id);

export const selectItem = (s, id) => s.items.find((it) => it.id === id) || null;
